import {
  Box,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { OrganizationScope, RegionScope } from "~/hooks/useScope";
import { Policy, PolicyAction } from "~/operations";
import { Flex } from "~/components/Flex";
import { Caption } from "~/pages/security/policies/components/Caption";
import { ShowMoreSummary } from "~/components/ShowMore/ShowMoreSummary";
import { PolicyAssignSelect } from "~/components/policy-gql/PolicyAssignSelect";
import { CircularIconButton } from "~/components/CircularIconButton/CircularIconButton";
import { IconButtonType } from "~/components/CircularIconButton/DynamicButtonIcon";
import { copyToClipboard } from "~/lib/clipboard";
import { LinkIcon } from "~/icons";
import { Email as EmailIcon2 } from "@mui/icons-material";
import { PolicyAssignButton } from "~/pages/security/policies/components/PolicyAssignButton";

interface PlatformPolicyHeaderProps {
  scope: RegionScope | OrganizationScope;
  policy: Partial<Policy>;
}

export function PlatformPolicyHeader({
  scope,
  policy,
}: PlatformPolicyHeaderProps) {
  const [shareAnchorEl, setShareAnchorEl] = useState<null | HTMLElement>(null);
  const shareOpen = Boolean(shareAnchorEl);
  const shareLink = window.location.href;
  const shareEmailLink = `mailto:hello@mondoo.com?subject=${encodeURIComponent("Take a look at this policy")}&body=${encodeURIComponent(shareLink)}`;

  return (
    <>
      <Box
        id="policy-header"
        mb={3}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Flex flexDirection="column">
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            textTransform="uppercase"
            sx={{ mb: 1 }}
          >
            {policy.name}
          </Typography>
          <Caption
            trustLevel={policy.trustLevel}
            version={policy.version}
            authors={policy.authors}
          />
        </Flex>
        <Flex id="policy-actions" alignItems="center" gap={4}>
          <Tooltip title="Share" placement="top" arrow>
            <CircularIconButton
              onClick={(e) => setShareAnchorEl(e.currentTarget)}
              sx={{ minWidth: "unset" }}
              title="Share"
              iconType={IconButtonType.IosShare}
            />
          </Tooltip>
          <Menu
            anchorEl={shareAnchorEl}
            open={shareOpen}
            onClose={() => setShareAnchorEl(null)}
            onClick={() => setShareAnchorEl(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <MenuItem
              onClick={() => {
                copyToClipboard(shareLink);
              }}
            >
              <ListItemIcon>
                <LinkIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Copy link</ListItemText>
            </MenuItem>
            <MenuItem
              component={Link}
              href={shareEmailLink}
              target="_blank"
              rel="noopener"
            >
              <ListItemIcon>
                <EmailIcon2 fontSize="small" />
              </ListItemIcon>
              <ListItemText>Email</ListItemText>
            </MenuItem>
          </Menu>
          {policy.mrn && (
            <PolicyAssignButton
              scope={scope}
              policyMrn={policy.mrn}
              action={policy.action}
              assigned={Boolean(policy.assigned)}
              assignedScope={policy.assignedScope}
              advanced
            />
          )}
        </Flex>
      </Box>
      {policy.summary && (
        <ShowMoreSummary
          maxHeight={165}
          text={policy.summary}
          externalLinksInNew
        />
      )}
    </>
  );
}
